import * as React from 'react';

import type { UseWidgetContentObserverOptions } from './useWidgetContentObserver.types';

import { getHasVisibleContent } from '../../PrecheckinWidget.utils';

/**
 * Hook to track widget content visibility with MutationObserver
 *
 * Monitors DOM changes in the widget container and updates visibility state
 * automatically when content is added/removed
 */
const useWidgetContentObserver = ({
  isMessageWidget,
  containerRef,
  shouldObserve,
}: Readonly<UseWidgetContentObserverOptions>): boolean => {
  const [hasWidgetContent, setHasWidgetContent] = React.useState(false);

  const updateWidgetContentState = React.useCallback((): void => {
    if (isMessageWidget) {
      setHasWidgetContent(true);
      return;
    }

    const widgetContainerElement = containerRef.current;

    if (!widgetContainerElement) {
      setHasWidgetContent(false);
      return;
    }

    setHasWidgetContent(getHasVisibleContent(widgetContainerElement));
  }, [isMessageWidget, containerRef]);

  React.useEffect(() => {
    if (!shouldObserve) {
      return;
    }

    updateWidgetContentState();

    const widgetContainerElement = containerRef.current;

    if (!widgetContainerElement) {
      return;
    }

    const observer = new MutationObserver(() => {
      updateWidgetContentState();
    });

    observer.observe(widgetContainerElement, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [shouldObserve, containerRef, updateWidgetContentState]);

  return hasWidgetContent;
};

export { useWidgetContentObserver };
